import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  updateProfile: (data) => api.put('/auth/profile', data),
  addAddress: (data) => api.post('/auth/addresses', data),
  deleteAddress: (addressId) => api.delete(`/auth/addresses/${addressId}`),
};

// Products API
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getFeatured: (limit = 8) => api.get('/products/featured', { params: { limit } }),
  getNew: (limit = 8) => api.get('/products/new', { params: { limit } }),
  getCategories: () => api.get('/products/categories'),
  getBySlug: (slug) => api.get(`/products/${slug}`),
};

// Technologies API
export const technologiesApi = {
  getAll: () => api.get('/technologies'),
  getBySlug: (slug) => api.get(`/technologies/${slug}`),
  getProducts: (slug, params) => api.get(`/technologies/${slug}/products`, { params }),
};

// Categories API
export const categoriesApi = {
  getAll: () => api.get('/categories'),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
};

// Campaigns API
export const campaignsApi = {
  getActive: () => api.get('/campaigns/active'),
  validate: (code, orderTotal) => api.post('/campaigns/validate', null, { params: { code, order_total: orderTotal } }),
};

// Cart API
export const cartApi = {
  get: () => api.get('/cart'),
  add: (item) => api.post('/cart/add', item),
  update: (variantSku, quantity) => api.put('/cart/update', null, { params: { variant_sku: variantSku, quantity } }),
  remove: (variantSku) => api.delete(`/cart/remove/${variantSku}`),
  clear: () => api.delete('/cart/clear'),
};

// Orders API
export const ordersApi = {
  create: (data) => api.post('/orders', data),
  getAll: (params) => api.get('/orders', { params }),
  getById: (orderId) => api.get(`/orders/${orderId}`),
};

// Admin API
export const adminApi = {
  // Dashboard
  getStats: () => api.get('/admin/stats'),
  
  // Products
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (productId, data) => api.put(`/admin/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/admin/products/${productId}`),
  importProducts: (products) => api.post('/admin/products/import', products),
  
  // Orders
  getOrders: (params) => api.get('/admin/orders', { params }),
  updateOrder: (orderId, data) => api.put(`/admin/orders/${orderId}`, data),
  
  // Technologies
  getTechnologies: () => api.get('/admin/technologies'),
  createTechnology: (data) => api.post('/admin/technologies', data),
  updateTechnology: (techId, data) => api.put(`/admin/technologies/${techId}`, data),
  deleteTechnology: (techId) => api.delete(`/admin/technologies/${techId}`),
  
  // Campaigns
  getCampaigns: () => api.get('/admin/campaigns'),
  createCampaign: (data) => api.post('/admin/campaigns', data),
  updateCampaign: (campaignId, data) => api.put(`/admin/campaigns/${campaignId}`, data),
  deleteCampaign: (campaignId) => api.delete(`/admin/campaigns/${campaignId}`),
  
  // Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (categoryId, data) => api.put(`/admin/categories/${categoryId}`, data),
  
  // Integrations
  getIntegrations: () => api.get('/admin/integrations'),
  createIntegration: (data) => api.post('/admin/integrations', data),
  updateIntegration: (provider, data) => api.put(`/admin/integrations/${provider}`, data),

  // Stores
  getStores: () => api.get('/admin/stores'),
  createStore: (data) => api.post('/admin/stores', data),
  updateStore: (storeId, data) => api.put(`/admin/stores/${storeId}`, data),
  deleteStore: (storeId) => api.delete(`/admin/stores/${storeId}`),

  // Showcase Sections
  getShowcaseSections: () => api.get('/admin/showcase/sections'),
  createShowcaseSection: (data) => api.post('/admin/showcase/sections', data),
  updateShowcaseSection: (sectionId, data) => api.put(`/admin/showcase/sections/${sectionId}`, data),
  deleteShowcaseSection: (sectionId) => api.delete(`/admin/showcase/sections/${sectionId}`),

  // Use Cases
  getUseCases: () => api.get('/admin/usecases'),
  createUseCase: (data) => api.post('/admin/usecases', data),
  updateUseCase: (usecaseId, data) => api.put(`/admin/usecases/${usecaseId}`, data),
  deleteUseCase: (usecaseId) => api.delete(`/admin/usecases/${usecaseId}`),
};

// Showcase API (Public)
export const showcaseApi = {
  getSections: () => api.get('/showcase/sections'),
  getSection: (sectionType) => api.get(`/showcase/sections/${sectionType}`),
};

// Use Cases API (Public)
export const usecasesApi = {
  getAll: () => api.get('/usecases'),
};

// Stores API (Public)
export const storesApi = {
  getAll: () => axios.get(`${API_BASE.replace('/api', '')}/api/stores`),
};

// Payment API
export const paymentApi = {
  init: (data) => api.post('/payment/init', data),
};

export default api;
